// 2026-07-25:新的 /media 真实页 — 直接管理当前 tenant WordPress Media Library。
//
// 设计原则:
//   - 不持有任何 mock 数据,任何展示项都来自 API
//   - API 失败显示 error + 重新加载,绝不回退到 mock
//   - WordPress 未连接 → 显示明确连接提示(不显示 demo 内容)
//   - 空数据 → Empty State
//   - 加载中 → Loading State
//   - 上传 / 删除走真实 API,删除二次确认
//
// UI 布局完全沿用旧 MediaDemoPage 的 .media-shell + 双 Card 结构,保证视觉一致;
// 仅替换数据源 + 加 Loading/Empty/Error/Connect 三态分支。

import { useEffect, useRef, useState } from "react";
import {
  Search,
  Upload,
  FolderClosed,
  Image as ImageIcon,
  FileText,
  Video,
  Music,
  File as FileIcon,
  Grid3x3,
  List,
  Trash2,
  Download,
  RefreshCw,
  AlertCircle,
  RefreshCcw,
  Settings as SettingsIcon,
  ExternalLink,
} from "lucide-react";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import {
  deleteMedia,
  refreshMediaCache,
  uploadMedia,
  type MediaCategory,
  type MediaItem,
} from "./api/mediaApi";
import { useMediaList, type LoadState } from "./hooks/useMediaList";
import { MediaPreviewModal } from "./components/MediaPreviewModal";

const TYPE_LABELS: Record<string, string> = {
  image: "画像",
  video: "動画",
  audio: "音声",
  file: "ファイル",
};

// ---- Download button (卡片上 + 预览 modal 上共用 anchor 打开 source_url) ----
//
// 2026-07-25 简化:
// 不做服务端流式代理 / fetch + Blob / window.open fallback。
// 直接 window.open(source_url, '_blank') 打开 WordPress 真实文件 URL。
// 浏览器默认行为:图片/视频直接显示,需要保存用浏览器自身的「名前を付けて保存」。

function DownloadButton({
  item,
  stopBubble,
  variant = "icon",
}: {
  item: MediaItem;
  stopBubble: (e: React.SyntheticEvent) => void;
  variant?: "icon" | "full";
}) {
  const isDisabled = !item.source_url;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDisabled || !item.source_url) return;
    // 直接打开 WordPress 真实文件 URL,浏览器按 content-type 处理
    window.open(item.source_url, "_blank", "noopener,noreferrer");
  };

  if (variant === "full") {
    return (
      <a
        className={`media-state-cta ${isDisabled ? "media-state-cta--disabled" : ""}`}
        href={isDisabled ? undefined : item.source_url ?? undefined}
        onClick={handleClick}
        onPointerDown={stopBubble}
        onMouseDown={stopBubble}
        aria-disabled={isDisabled}
        data-testid="media-download-full"
        role="button"
      >
        <Download size={14} />
        <span>ダウンロード</span>
      </a>
    );
  }

  return (
    <button
      type="button"
      className="media-card-action media-card-action--download"
      title="ダウンロード"
      onClick={handleClick as unknown as React.MouseEventHandler<HTMLButtonElement>}
      onPointerDown={stopBubble}
      onMouseDown={stopBubble}
      disabled={isDisabled}
      aria-label="ダウンロード"
      data-testid="media-card-download"
    >
      <Download size={14} color="#fff" />
    </button>
  );
}

function pickIcon(mt: string | undefined) {
  switch (mt) {
    case "image":
      return <ImageIcon size={32} color="rgba(255,255,255,0.85)" />;
    case "video":
      return <Video size={32} color="rgba(255,255,255,0.85)" />;
    case "audio":
      return <Music size={32} color="rgba(255,255,255,0.85)" />;
    default:
      return <FileIcon size={32} color="rgba(255,255,255,0.85)" />;
  }
}

function extFromItem(it: MediaItem): string {
  if (it.filename) {
    const m = /\.([a-zA-Z0-9]+)$/.exec(it.filename);
    if (m) return m[1].toUpperCase();
  }
  if (it.mime_type) {
    const m = /\/([a-zA-Z0-9.+-]+)$/.exec(it.mime_type);
    if (m) return m[1].toUpperCase();
  }
  return "FILE";
}

function formatSize(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// 2026-07-25:Media 库总容量格式化(KB / MB / GB,1 位小数)
function formatTotalSize(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n) || n <= 0) return "—";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  // 2026-07-25T10:00:00 → 2026-07-25
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  return m ? m[1] : iso;
}

// ---- Connect prompt (WP not configured) ----

function ConnectPrompt({ code, message }: { code: string; message: string }) {
  return (
    <Card>
      <div className="media-state media-state--connect" data-testid="media-connect-prompt">
        <div className="media-state-icon">
          <SettingsIcon size={36} color="#94a3b8" />
        </div>
        <div className="media-state-title">WordPress 接続が未設定です</div>
        <div className="media-state-desc">
          メディアライブラリを表示するには、現在のテナントに WordPress 接続情報が必要です。
          システム設定ページから接続を行ってください。
        </div>
        <div className="media-state-detail" role="alert">
          <code>{code}</code> — {message}
        </div>
        <a href="/settings" className="media-state-cta">
          <SettingsIcon size={14} />
          <span>設定ページへ</span>
        </a>
      </div>
    </Card>
  );
}

// ---- Generic state boxes ----

function ErrorBox({ code, message, onRetry }: { code: string; message: string; onRetry: () => void }) {
  return (
    <Card>
      <div className="media-state media-state--error" data-testid="media-error">
        <div className="media-state-icon">
          <AlertCircle size={36} color="#ef4444" />
        </div>
        <div className="media-state-title">メディアを取得できませんでした</div>
        <div className="media-state-desc" role="alert">
          <code>{code}</code> — {message}
        </div>
        <button className="media-state-cta" onClick={onRetry} type="button">
          <RefreshCw size={14} />
          <span>再読み込み</span>
        </button>
      </div>
    </Card>
  );
}

function LoadingGrid({ count = 8 }: { count?: number }) {
  return (
    <div className="media-grid" data-testid="media-loading">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="media-card media-card--skeleton">
          <div className="media-thumb media-thumb--skeleton" />
          <div className="media-info">
            <div className="media-skeleton-line" />
            <div className="media-skeleton-line media-skeleton-line--short" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onUploadClick }: { onUploadClick: () => void }) {
  return (
    <Card>
      <div className="media-state media-state--empty" data-testid="media-empty">
        <div className="media-state-icon">
          <ImageIcon size={36} color="#94a3b8" />
        </div>
        <div className="media-state-title">メディアがまだありません</div>
        <div className="media-state-desc">
          最初のメディアをアップロードすると、ここに表示されます。
        </div>
        <button className="media-state-cta" onClick={onUploadClick} type="button">
          <Upload size={14} />
          <span>メディアをアップロード</span>
        </button>
      </div>
    </Card>
  );
}

// ---- Media card ----

function MediaCardView({
  item,
  canDelete,
  onDelete,
  onOpen,
}: {
  item: MediaItem;
  canDelete: boolean;
  onDelete: (id: number) => void;
  onOpen: (item: MediaItem) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const ext = extFromItem(item);
  const isImage = item.media_type === "image";

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen(item);
    }
  };

  // 事件拦截辅助:在 capture 阶段就停止冒泡,确保任何来源(mouse / pointer /
  // touch / keyboard)的动作按钮事件都不会触发卡片 onClick/onKeyDown。
  // preventDefault + stopPropagation 写在最前面,状态更新是副作用。
  const stopBubble = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      className="media-card"
      data-testid="media-card"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={handleCardKeyDown}
    >
      <div className="media-thumb" data-type={item.media_type}>
        {isImage && item.thumbnail_url ? (
          // eslint-disable-next-line jsx-a11y/alt-text
          <img
            className="media-thumb-img"
            src={item.thumbnail_url}
            alt={item.alt_text || item.title || ""}
            loading="lazy"
            onError={(e) => {
              // 缩略图加载失败 → 降级到 icon
              const el = e.currentTarget;
              el.style.display = "none";
              el.parentElement?.classList.add("media-thumb--fallback");
            }}
          />
        ) : null}
        {(!isImage || !item.thumbnail_url) && (
          <div className="media-thumb-fallback">{pickIcon(item.media_type)}</div>
        )}
        <span className="media-thumb-ext">{ext}</span>
        <div className="media-card-actions">
          {canDelete && (
            <button
              type="button"
              className="media-card-action media-card-action--delete"
              title="削除"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setConfirming(true);
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              aria-label="削除"
              data-testid="media-card-delete"
            >
              <Trash2 size={14} color="#fff" />
            </button>
          )}
          <DownloadButton item={item} stopBubble={stopBubble} />
        </div>
      </div>
      <div className="media-info">
        <div className="media-name" title={item.title || item.filename || `#${item.id}`}>
          {item.title || item.filename || `#${item.id}`}
        </div>
        <div className="media-meta">
          <span>{formatDate(item.created_at)}</span>
          <span>{formatSize(item.file_size)}</span>
        </div>
      </div>
      {confirming && (
        // confirm 浮层不冒泡到卡片(否则 click confirm-body 也会触发 onOpen)。
        // 用 capture 阶段 stopPropagation + onClick stopPropagation 双保险。
        <div
          className="media-confirm"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
        >
          <div className="media-confirm-body">
            <div className="media-confirm-title">このメディアを削除しますか?</div>
            <div className="media-confirm-desc">
              {item.title || item.filename || `#${item.id}`} を WordPress から完全に削除します。元に戻せません。
            </div>
            <div className="media-confirm-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setConfirming(false);
                }}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setConfirming(false);
                  onDelete(item.id);
                }}
                data-testid="media-card-confirm-delete"
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Type filter ----

const TYPE_FILTERS: { value: MediaCategory; label: string }[] = [
  { value: "", label: "すべて" },
  { value: "image", label: "画像" },
  { value: "video", label: "動画" },
  { value: "audio", label: "音声" },
  { value: "file", label: "ファイル" },
];

// ---- Main page ----

export function MediaPage() {
  const { state, params, setParams, refresh } = useMediaList({ per_page: 24 });
  const [searchInput, setSearchInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefreshStarted, setAutoRefreshStarted] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // 简易防抖:300ms 后把搜索词 push 到 params(避免每按一键就 fetch)
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.search ?? "") !== searchInput) {
        setParams({ ...params, search: searchInput, page: 1 });
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    if (state.status !== "ready" || autoRefreshStarted) return;
    if (!state.syncMeta?.stale) return;
    setAutoRefreshStarted(true);
    void (async () => {
      setRefreshing(true);
      try {
        await refreshMediaCache(false);
        refresh();
      } finally {
        setRefreshing(false);
      }
    })();
  }, [autoRefreshStarted, refresh, state]);

  const forceRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshMediaCache(true);
      refresh();
    } finally {
      setRefreshing(false);
    }
  };

  // 上传成功后 → 刷新列表
  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (uploading) return; // 防重复点击
    setUploading(true);
    try {
      await uploadMedia({ file, title: file.name.replace(/\.[^.]+$/, "") });
      await refreshMediaCache(true);
      refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      window.alert(`アップロードに失敗しました: ${msg}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMedia(id);
      await refreshMediaCache(true);
      refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      window.alert(`削除に失敗しました: ${msg}`);
    }
  };

  const canWrite =
    // 通过 state 间接判断:有 ready 就认为权限已通过;装饰器层在 server 端已经处理 viewer 写操作 403
    state.status === "ready" || state.status === "loading" || state.status === "error";

  // ---- 渲染:状态机分支 ----

  const renderList = () => {
    if (state.status === "loading") return <LoadingGrid />;
    if (state.status === "error") {
      // 区分 WP 未配置 vs 通用错误
      if (state.code === "AUTH_WP_NOT_CONFIGURED") {
        return <ConnectPrompt code={state.code} message={state.message} />;
      }
      return <ErrorBox code={state.code} message={state.message} onRetry={refresh} />;
    }
    // ready
    if (state.items.length === 0) {
      return <EmptyState onUploadClick={() => fileInputRef.current?.click()} />;
    }
    return (
      <div className="media-grid" data-testid="media-grid">
        {state.items.map((it) => (
          <MediaCardView
            key={it.id}
            item={it}
            canDelete={canWrite}
            onDelete={handleDelete}
            onOpen={setSelectedMedia}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="media-shell">
      {/* 左: タイプ分類(Documents / Other 因 WP 映射困难,先只显示 5 种已知 media_type) */}
      <Card>
        <div className="card-title" style={{ marginBottom: 12 }}>
          カテゴリ
        </div>
        <div>
          {TYPE_FILTERS.map((t) => {
            const active = (params.category ?? "") === t.value;
            return (
              <div
                key={t.value || "all"}
                className={`folder-item ${active ? "active" : ""}`}
                onClick={() => setParams({ ...params, category: t.value, page: 1 })}
                data-testid={`media-type-${t.value || "all"}`}
              >
                <FolderClosed size={14} />
                <span>{t.label}</span>
              </div>
            );
          })}
        </div>

        <hr className="divider" />

        <Button
          full
          variant="secondary"
          iconLeft={<RefreshCcw size={14} />}
          onClick={() => void forceRefresh()}
          disabled={state.status === "loading" || refreshing}
        >
          {refreshing ? "更新中..." : "今すぐ更新"}
        </Button>

        <hr className="divider" />

        <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 10 }}>
          接続情報
        </div>
        <div style={{ fontSize: 12, color: "#374151", marginBottom: 4 }}>
          <strong style={{ color: "#111827" }}>WordPress</strong> Media Library
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>
          {state.status === "ready"
            ? `${state.pagination.total} 件のメディア`
            : state.status === "error" && state.code === "AUTH_WP_NOT_CONFIGURED"
              ? "未接続"
              : "接続中"}
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>
          約 {formatTotalSize(state.status === "ready" ? state.libraryStats?.total_size_bytes : null)}
        </div>
      </Card>

      {/* 右: メディアエリア */}
      <Card>
        <div className="row-between mb-18">
          <div className="row gap-8">
            <div className="input-with-icon" style={{ width: 280 }}>
              <span className="input-icon">
                <Search size={14} />
              </span>
              <input
                className="input"
                placeholder="ファイル名で検索..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                data-testid="media-search-input"
              />
            </div>
            <select
              className="select"
              style={{ width: 130 }}
              value={params.category ?? ""}
              onChange={(e) =>
                setParams({ ...params, category: e.target.value as MediaCategory, page: 1 })
              }
              data-testid="media-type-select"
            >
              {TYPE_FILTERS.map((t) => (
                <option key={t.value || "all"} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="row gap-8">
            <button className="btn-icon" disabled title="グリッド" data-testid="media-view-grid">
              <Grid3x3 size={16} />
            </button>
            <button className="btn-icon" disabled title="リスト(将来用)" data-testid="media-view-list">
              <List size={16} />
            </button>
            <Button
              iconLeft={<Upload size={14} />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || !canWrite}
              data-testid="media-upload-button"
            >
              {uploading ? "アップロード中..." : "アップロード"}
            </Button>
            <input
              ref={fileInputRef as React.RefObject<HTMLInputElement>}
              type="file"
              style={{ display: "none" }}
              aria-hidden
              onChange={(e) => handleUpload(e.target.files)}
              data-testid="media-upload-input"
            />
          </div>
        </div>

        {renderList()}
      </Card>

      <MediaPreviewModal
        item={selectedMedia}
        onClose={() => setSelectedMedia(null)}
      />
    </div>
  );
}

// 避免 linter unused(LoadState 仅作类型 export)
export type { LoadState };
